// Port of CoreModels/Geo/GeoMath.swift and the geometry helpers of GeoPrimitives.swift.
// Spherical-earth formulas are accurate to ~0.3 %, well within consumer GPS error for hiking.

import type { GeoBounds, GeoCoordinate, GeoPolygon, GeoPolyline, RoutePoint } from './models';

export const earthRadius = 6_371_008.8;
export const metersPerDegreeLatitude = (earthRadius * Math.PI) / 180;

export const degreesToRadians = (d: number): number => (d * Math.PI) / 180;
export const radiansToDegrees = (r: number): number => (r * 180) / Math.PI;

export function metersPerDegreeLongitude(latitude: number): number {
  return metersPerDegreeLatitude * Math.cos(degreesToRadians(latitude));
}

export function coord(latitude: number, longitude: number): GeoCoordinate {
  return { latitude, longitude };
}

export function isValidCoordinate(c: GeoCoordinate): boolean {
  return (
    Number.isFinite(c.latitude) &&
    Number.isFinite(c.longitude) &&
    c.latitude >= -90 &&
    c.latitude <= 90 &&
    c.longitude >= -180 &&
    c.longitude <= 180
  );
}

export function sameCoordinate(a: GeoCoordinate, b: GeoCoordinate): boolean {
  return a.latitude === b.latitude && a.longitude === b.longitude;
}

/** Great-circle (haversine) distance in metres. */
export function distance(a: GeoCoordinate, b: GeoCoordinate): number {
  const lat1 = degreesToRadians(a.latitude);
  const lat2 = degreesToRadians(b.latitude);
  const dLat = lat2 - lat1;
  const dLon = degreesToRadians(b.longitude - a.longitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * earthRadius * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** 3D distance including the elevation difference, in metres. */
export function distance3D(a: RoutePoint, b: RoutePoint): number {
  const flat = distance(a.coordinate, b.coordinate);
  if (a.elevation === undefined || b.elevation === undefined) return flat;
  const dz = b.elevation - a.elevation;
  return Math.sqrt(flat * flat + dz * dz);
}

/** Initial bearing from `a` to `b`, degrees clockwise from true north, 0..<360. */
export function bearing(a: GeoCoordinate, b: GeoCoordinate): number {
  const lat1 = degreesToRadians(a.latitude);
  const lat2 = degreesToRadians(b.latitude);
  const dLon = degreesToRadians(b.longitude - a.longitude);
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  const deg = radiansToDegrees(Math.atan2(y, x));
  return (deg + 360) % 360;
}

/** Destination point given a start, a bearing (degrees) and a distance (metres). */
export function destination(start: GeoCoordinate, bearingDegrees: number, distanceMeters: number): GeoCoordinate {
  const delta = distanceMeters / earthRadius;
  const theta = degreesToRadians(bearingDegrees);
  const lat1 = degreesToRadians(start.latitude);
  const lon1 = degreesToRadians(start.longitude);
  const lat2 = Math.asin(Math.sin(lat1) * Math.cos(delta) + Math.cos(lat1) * Math.sin(delta) * Math.cos(theta));
  const lon2 =
    lon1 + Math.atan2(Math.sin(theta) * Math.sin(delta) * Math.cos(lat1), Math.cos(delta) - Math.sin(lat1) * Math.sin(lat2));
  const lonDeg = ((radiansToDegrees(lon2) + 540) % 360) - 180;
  return coord(radiansToDegrees(lat2), lonDeg);
}

/** Linear interpolation between two coordinates (adequate for short segments). */
export function interpolate(a: GeoCoordinate, b: GeoCoordinate, t: number): GeoCoordinate {
  return coord(a.latitude + (b.latitude - a.latitude) * t, a.longitude + (b.longitude - a.longitude) * t);
}

/** Projects `c` to a local east/north plane (metres) centred on `origin`. */
export function localXY(c: GeoCoordinate, origin: GeoCoordinate): { x: number; y: number } {
  return {
    x: (c.longitude - origin.longitude) * metersPerDegreeLongitude(origin.latitude),
    y: (c.latitude - origin.latitude) * metersPerDegreeLatitude,
  };
}

export interface SegmentProjection {
  /** Fraction along the segment, 0…1. */
  fraction: number;
  /** Distance from the point to the projected point, metres. */
  distance: number;
  coordinate: GeoCoordinate;
}

/** Projects `p` onto segment `a`→`b` with a local planar approximation. */
export function projectOntoSegment(p: GeoCoordinate, a: GeoCoordinate, b: GeoCoordinate): SegmentProjection {
  const pa = localXY(a, p);
  const pb = localXY(b, p);
  const dx = pb.x - pa.x;
  const dy = pb.y - pa.y;
  const len2 = dx * dx + dy * dy;
  let t = 0;
  if (len2 > 0) {
    t = ((0 - pa.x) * dx + (0 - pa.y) * dy) / len2;
    t = Math.min(1, Math.max(0, t));
  }
  const proj = interpolate(a, b, t);
  return { fraction: t, distance: distance(p, proj), coordinate: proj };
}

export function distanceToSegment(p: GeoCoordinate, a: GeoCoordinate, b: GeoCoordinate): number {
  return projectOntoSegment(p, a, b).distance;
}

export interface PolylineProjection {
  /** Index of the segment start vertex. */
  segmentIndex: number;
  fraction: number;
  /** Distance from the start of the polyline to the projected point, metres. */
  distanceAlong: number;
  /** Cross-track distance from the point to the polyline, metres. */
  crossTrackDistance: number;
  coordinate: GeoCoordinate;
}

/**
 * Projects `p` onto a polyline. `cumulative` must be the polyline's cumulative distances. `segmentRange`
 * ([start, end) of segment indices) restricts the search, e.g. for hysteresis on out-and-back routes.
 */
export function projectOntoPolyline(
  p: GeoCoordinate,
  points: readonly GeoCoordinate[],
  cumulative: readonly number[],
  segmentRange?: { start: number; end: number },
): PolylineProjection | undefined {
  if (points.length < 2) {
    const only = points[0];
    if (only) {
      return { segmentIndex: 0, fraction: 0, distanceAlong: 0, crossTrackDistance: distance(p, only), coordinate: only };
    }
    return undefined;
  }
  // Mismatched cumulative distances would give a meaningless distanceAlong: refuse instead of guessing.
  if (cumulative.length !== points.length) return undefined;
  const fullEnd = points.length - 1;
  const start = segmentRange ? Math.max(0, Math.min(fullEnd, segmentRange.start)) : 0;
  const end = segmentRange ? Math.max(0, Math.min(fullEnd, segmentRange.end)) : fullEnd;
  if (end <= start) return undefined;
  let best: PolylineProjection | undefined;
  for (let i = start; i < end; i++) {
    const proj = projectOntoSegment(p, points[i], points[i + 1]);
    if (best === undefined || proj.distance < best.crossTrackDistance) {
      const segLen = cumulative[i + 1] - cumulative[i];
      best = {
        segmentIndex: i,
        fraction: proj.fraction,
        distanceAlong: cumulative[i] + segLen * proj.fraction,
        crossTrackDistance: proj.distance,
        coordinate: proj.coordinate,
      };
    }
  }
  return best;
}

/** Coordinate at a distance along a polyline (clamped to its ends). */
export function coordinateAtDistance(
  d: number,
  points: readonly GeoCoordinate[],
  cumulative: readonly number[],
): GeoCoordinate | undefined {
  const first = points[0];
  if (!first) return undefined;
  if (points.length === 1) return first;
  if (cumulative.length !== points.length) return undefined;
  const total = cumulative[cumulative.length - 1];
  if (d <= 0) return first;
  if (d >= total) return points[points.length - 1];
  let lo = 0;
  let hi = cumulative.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (cumulative[mid] <= d) lo = mid;
    else hi = mid;
  }
  const segLen = cumulative[hi] - cumulative[lo];
  const t = segLen > 0 ? (d - cumulative[lo]) / segLen : 0;
  return interpolate(points[lo], points[hi], t);
}

// MARK: Rings and polygons

/** Removes a duplicated closing vertex if present. */
export function openRing(ring: readonly GeoCoordinate[]): GeoCoordinate[] {
  if (ring.length > 1 && sameCoordinate(ring[0], ring[ring.length - 1])) return ring.slice(0, -1);
  return ring.slice();
}

/** Even–odd point-in-ring test in lon/lat space. */
export function ringContains(ring: readonly GeoCoordinate[], p: GeoCoordinate): boolean {
  const r = openRing(ring);
  if (r.length < 3) return false;
  let inside = false;
  let j = r.length - 1;
  for (let i = 0; i < r.length; i++) {
    const xi = r[i].longitude;
    const yi = r[i].latitude;
    const xj = r[j].longitude;
    const yj = r[j].latitude;
    if (yi > p.latitude !== yj > p.latitude) {
      const xCross = ((xj - xi) * (p.latitude - yi)) / (yj - yi) + xi;
      if (p.longitude < xCross) inside = !inside;
    }
    j = i;
  }
  return inside;
}

/** Signed area (m²) of a ring, local projection around its first vertex. */
export function ringSignedArea(ring: readonly GeoCoordinate[]): number {
  const r = openRing(ring);
  if (r.length < 3) return 0;
  const origin = r[0];
  const xy = r.map((c) => localXY(c, origin));
  let sum = 0;
  for (let i = 0; i < xy.length; i++) {
    const a = xy[i];
    const b = xy[(i + 1) % xy.length];
    sum += a.x * b.y - b.x * a.y;
  }
  return sum / 2;
}

/** Minimum distance (m) from a point to any edge of a ring. */
export function distanceToRing(ring: readonly GeoCoordinate[], p: GeoCoordinate): number {
  const r = openRing(ring);
  if (r.length === 0) return Infinity;
  if (r.length === 1) return distance(p, r[0]);
  let best = Infinity;
  for (let i = 0; i < r.length; i++) {
    best = Math.min(best, distanceToSegment(p, r[i], r[(i + 1) % r.length]));
  }
  return best;
}

/** Convex hull (Andrew's monotone chain) in lon/lat space; counter-clockwise ring. */
export function convexHull(coordinates: readonly GeoCoordinate[]): GeoCoordinate[] {
  const seen = new Set<string>();
  const pts: GeoCoordinate[] = [];
  for (const c of coordinates) {
    const key = `${c.latitude},${c.longitude}`;
    if (!seen.has(key)) {
      seen.add(key);
      pts.push(c);
    }
  }
  pts.sort((a, b) => (a.longitude === b.longitude ? a.latitude - b.latitude : a.longitude - b.longitude));
  if (pts.length <= 2) return pts;
  const cross = (o: GeoCoordinate, a: GeoCoordinate, b: GeoCoordinate) =>
    (a.longitude - o.longitude) * (b.latitude - o.latitude) - (a.latitude - o.latitude) * (b.longitude - o.longitude);
  const lower: GeoCoordinate[] = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: GeoCoordinate[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

/** Douglas–Peucker simplification with a tolerance in metres; elevations are kept on kept points. */
export function simplify(points: readonly RoutePoint[], toleranceMeters: number): RoutePoint[] {
  if (points.length <= 2 || !(toleranceMeters > 0)) return points.slice();
  const keep = new Array<boolean>(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;
  const stack: Array<[number, number]> = [[0, points.length - 1]];
  while (stack.length > 0) {
    const [s, e] = stack.pop()!;
    if (e <= s + 1) continue;
    let maxD = 0;
    let idx = s;
    for (let i = s + 1; i < e; i++) {
      const d = distanceToSegment(points[i].coordinate, points[s].coordinate, points[e].coordinate);
      if (d > maxD) {
        maxD = d;
        idx = i;
      }
    }
    if (maxD > toleranceMeters) {
      keep[idx] = true;
      stack.push([s, idx], [idx, e]);
    }
  }
  return points.filter((_, i) => keep[i]);
}

// MARK: Bounds (GeoBounds methods)

export function boundsOf(coordinates: Iterable<GeoCoordinate>): GeoBounds | undefined {
  let b: GeoBounds | undefined;
  for (const c of coordinates) {
    if (!b) {
      b = { minLatitude: c.latitude, minLongitude: c.longitude, maxLatitude: c.latitude, maxLongitude: c.longitude };
    } else {
      if (c.latitude < b.minLatitude) b.minLatitude = c.latitude;
      if (c.latitude > b.maxLatitude) b.maxLatitude = c.latitude;
      if (c.longitude < b.minLongitude) b.minLongitude = c.longitude;
      if (c.longitude > b.maxLongitude) b.maxLongitude = c.longitude;
    }
  }
  return b;
}

export function boundsCenter(b: GeoBounds): GeoCoordinate {
  return coord((b.minLatitude + b.maxLatitude) / 2, (b.minLongitude + b.maxLongitude) / 2);
}

export function boundsContains(b: GeoBounds, c: GeoCoordinate): boolean {
  return (
    c.latitude >= b.minLatitude && c.latitude <= b.maxLatitude && c.longitude >= b.minLongitude && c.longitude <= b.maxLongitude
  );
}

export function boundsIntersect(a: GeoBounds, b: GeoBounds): boolean {
  return !(
    b.minLatitude > a.maxLatitude ||
    b.maxLatitude < a.minLatitude ||
    b.minLongitude > a.maxLongitude ||
    b.maxLongitude < a.minLongitude
  );
}

export function boundsUnion(a: GeoBounds, b: GeoBounds): GeoBounds {
  return {
    minLatitude: Math.min(a.minLatitude, b.minLatitude),
    minLongitude: Math.min(a.minLongitude, b.minLongitude),
    maxLatitude: Math.max(a.maxLatitude, b.maxLatitude),
    maxLongitude: Math.max(a.maxLongitude, b.maxLongitude),
  };
}

/** Expands the bounds by approximately `meters` in every direction. */
export function boundsExpanded(b: GeoBounds, meters: number): GeoBounds {
  const dLat = meters / metersPerDegreeLatitude;
  const midLat = (b.minLatitude + b.maxLatitude) / 2;
  const dLon = meters / Math.max(metersPerDegreeLongitude(midLat), 1);
  return {
    minLatitude: Math.max(-90, b.minLatitude - dLat),
    minLongitude: Math.max(-180, b.minLongitude - dLon),
    maxLatitude: Math.min(90, b.maxLatitude + dLat),
    maxLongitude: Math.min(180, b.maxLongitude + dLon),
  };
}

/** Switzerland and Liechtenstein, generously padded (chooses the Swiss map provider). */
export const switzerlandBounds: GeoBounds = { minLatitude: 45.8, minLongitude: 5.9, maxLatitude: 47.84, maxLongitude: 10.53 };
/** Approximate extent of the Alpine arc. */
export const alpsBounds: GeoBounds = { minLatitude: 43.5, minLongitude: 5.0, maxLatitude: 48.5, maxLongitude: 16.5 };

// MARK: Polygons (GeoPolygon methods)

export function polygonBounds(p: GeoPolygon): GeoBounds | undefined {
  return boundsOf(p.exterior);
}

/** Point-in-polygon (even–odd) honouring holes. */
export function polygonContains(p: GeoPolygon, c: GeoCoordinate): boolean {
  if (p.exterior.length < 3) return false;
  const b = polygonBounds(p);
  if (b && !boundsContains(b, c)) return false;
  if (!ringContains(p.exterior, c)) return false;
  for (const hole of p.holes) {
    if (hole.length >= 3 && ringContains(hole, c)) return false;
  }
  return true;
}

export function polygonArea(p: GeoPolygon): number {
  if (p.exterior.length < 3) return 0;
  let area = Math.abs(ringSignedArea(p.exterior));
  for (const hole of p.holes) if (hole.length >= 3) area -= Math.abs(ringSignedArea(hole));
  return Math.max(0, area);
}

/** Arithmetic centroid of the exterior ring's vertices. */
export function polygonCentroid(p: GeoPolygon): GeoCoordinate {
  const ring = openRing(p.exterior);
  if (ring.length === 0) return coord(0, 0);
  let lat = 0;
  let lon = 0;
  for (const c of ring) {
    lat += c.latitude;
    lon += c.longitude;
  }
  return coord(lat / ring.length, lon / ring.length);
}

/** Distance (m) from a coordinate to the nearest polygon edge; 0 when inside. */
export function polygonDistance(p: GeoPolygon, c: GeoCoordinate): number {
  if (polygonContains(p, c)) return 0;
  let best = distanceToRing(p.exterior, c);
  for (const hole of p.holes) if (hole.length >= 3) best = Math.min(best, distanceToRing(hole, c));
  return best;
}

/** Regular polygon approximating a circle. */
export function circlePolygon(center: GeoCoordinate, radiusMeters: number, segments = 24): GeoPolygon {
  const n = Math.max(6, segments);
  const exterior: GeoCoordinate[] = [];
  for (let i = 0; i < n; i++) exterior.push(destination(center, (i * 360) / n, radiusMeters));
  return { exterior, holes: [] };
}

export function rectanglePolygon(b: GeoBounds): GeoPolygon {
  return {
    exterior: [
      coord(b.minLatitude, b.minLongitude),
      coord(b.minLatitude, b.maxLongitude),
      coord(b.maxLatitude, b.maxLongitude),
      coord(b.maxLatitude, b.minLongitude),
    ],
    holes: [],
  };
}

// MARK: Polylines (GeoPolyline methods)

export function polylineCoordinates(line: GeoPolyline): GeoCoordinate[] {
  return line.points.map((p) => p.coordinate);
}

export function polylineBounds(line: GeoPolyline): GeoBounds | undefined {
  return boundsOf(line.points.map((p) => p.coordinate));
}

export function polylineHasElevation(line: GeoPolyline): boolean {
  return line.points.length > 0 && line.points.every((p) => p.elevation !== undefined);
}

/** Total 2D length in metres. */
export function polylineLength(line: GeoPolyline): number {
  let total = 0;
  for (let i = 1; i < line.points.length; i++) total += distance(line.points[i - 1].coordinate, line.points[i].coordinate);
  return total;
}

/** Cumulative 2D distance (m) at each vertex; the first element is 0. */
export function cumulativeDistances(line: GeoPolyline): number[] {
  if (line.points.length === 0) return [];
  const result = [0];
  for (let i = 1; i < line.points.length; i++) {
    result.push(result[i - 1] + distance(line.points[i - 1].coordinate, line.points[i].coordinate));
  }
  return result;
}

export function polylineReversed(line: GeoPolyline): GeoPolyline {
  return { points: line.points.slice().reverse() };
}

/** Concatenates polylines, dropping a duplicated joint vertex when one ends where the next starts (within 1 m). */
export function joinPolylines(lines: readonly GeoPolyline[]): GeoPolyline {
  const out: RoutePoint[] = [];
  for (const line of lines) {
    const first = line.points[0];
    if (!first) continue;
    const last = out[out.length - 1];
    if (last && distance(last.coordinate, first.coordinate) < 1) out.push(...line.points.slice(1));
    else out.push(...line.points);
  }
  return { points: out };
}
